import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { StyleSheet, Text, View } from "react-native";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import PressableContainer from "webComponentsFormElements/PressableContainer.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  root: {
    gap: 10,
  },
  labelText: {
    fontSize: 18,
    color: theme.color.backgroundText,
  },
  labelSubtext: {
    fontSize: theme.font.size.small,
    color: theme.color.backgroundText,
  },
  labelTextBold: {
    fontWeight: theme.font.weight.semibold,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
    paddingVertical: 8,
    justifyContent: "space-between",
    backgroundColor: theme.color.background,
  },
});

export default function UserSelectionModal(props) {
  const { open, onClose, selectedUserIds, setSelectedUserIds } = props;

  const { organizationColor, personList } = useContext(OrganizationContext);
  const [searchText, setSearchText] = useState("");
  const [newSelectedUserIds, setNewSelectedUserIds] = useState(new Set());

  useEffect(
    () => setNewSelectedUserIds(new Set(selectedUserIds)),
    [selectedUserIds],
  );

  const onSave = useCallback(() => {
    if (
      newSelectedUserIds.symmetricDifference(new Set(selectedUserIds)).size ===
      0
    ) {
      onClose();
      return;
    }
    setSelectedUserIds([...newSelectedUserIds]);
    onClose();
  }, [newSelectedUserIds, setSelectedUserIds, onClose, selectedUserIds]);

  const toggleUser = useCallback(
    (userId) => {
      if (newSelectedUserIds.has(userId)) {
        newSelectedUserIds.delete(userId);
      } else {
        newSelectedUserIds.add(userId);
      }

      setNewSelectedUserIds(new Set(newSelectedUserIds));
    },
    [newSelectedUserIds],
  );

  const areAllUsersSelected = useMemo(() => {
    return personList.every((person) =>
      newSelectedUserIds.has(person.personId),
    );
  }, [personList, newSelectedUserIds]);

  const toggleSelectAllUsers = useCallback(() => {
    if (areAllUsersSelected) {
      setNewSelectedUserIds(new Set());
    } else {
      const allUserIds = personList.map((person) => person.personId);

      setNewSelectedUserIds(new Set(allUserIds));
    }
  }, [areAllUsersSelected, personList]);

  return (
    <CustomModal
      open={open}
      onClose={() => onSave()}
      title="Select Individuals"
      scroll
    >
      <View style={classes.root}>
        <SearchBar value={searchText} onChangeText={setSearchText} />

        <PressableContainer style={classes.row} onPress={toggleSelectAllUsers}>
          <Text style={[classes.labelText, classes.labelTextBold]}>
            All Individuals
          </Text>
          <Icon
            name={areAllUsersSelected ? "check" : "checkbox-blank-outline"}
            size={25}
            color={organizationColor}
          />
        </PressableContainer>

        <View>
          {personList
            .filter((person) =>
              `${person.firstName} ${person.lastName}`
                .toLowerCase()
                .includes(searchText.toLowerCase()),
            )
            .sort((a, b) => a.lastName.localeCompare(b.lastName))
            .map((user) => (
              <PressableContainer
                key={user.personId}
                style={classes.row}
                onPress={() => toggleUser(user.personId)}
              >
                <Text style={classes.labelText}>
                  {user.firstName} {user.lastName}
                </Text>
                {user.status !== "Active" && (
                  <Text
                    style={[
                      classes.labelSubtext,
                      { color: theme.color.yellow },
                    ]}
                  >
                    {user.status}
                  </Text>
                )}
                <Icon
                  name={
                    newSelectedUserIds.has(user.personId)
                      ? "check"
                      : "checkbox-blank-outline"
                  }
                  size={25}
                  color={organizationColor}
                />
              </PressableContainer>
            ))}
        </View>
      </View>
    </CustomModal>
  );
}
